import { useEffect, useMemo } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import {
    dropBoundingFromProtected,
    pruneProtectedToExisting,
} from '~/components/toolbar/tools/ContouringTools/drawingConstraints/collisionSelection';
import { currentStructureSetState } from '~/state/atoms/structureSet/structureSetAtoms';
import {
    boundingRoiConstraintState,
    collisionConstraintState,
} from '~/state/atoms/toolsAtoms';

/**
 * Keeps the Inside and Outside drawing constraints bound to structures of
 * the active structure set, whatever shows in the toolbar:
 *
 * - An Inside structure that leaves the set, or is a point ROI, leaves
 *   Inside with no structure, on or off. No other structure takes its
 *   place, so the Modifiers panel shows Inside as inactive until the user
 *   chooses one. The structure being edited stays the bound while selected,
 *   and the panel reads it as inactive.
 * - The active Inside structure leaves the protected set, as a stroke
 *   clipped to it and then cut by it would be erased.
 * - A protected structure that leaves the set leaves the protected set, so
 *   a structure that later takes its number is not protected unasked.
 *
 * Each update starts from the constraint's current value.
 */
export function useKeepDrawingConstraintsValid(): void {
    const structureSet = useRecoilValue(currentStructureSetState);
    const [bounding, setBounding] = useRecoilState(boundingRoiConstraintState);
    const [collision, setCollision] = useRecoilState(collisionConstraintState);

    const roiArray = useMemo(
        () => structureSet?.roiArray ?? [],
        [structureSet]
    );

    useEffect(() => {
        setBounding((c) =>
            c.roiNumber === null ||
            roiArray.some((r) => !r.isPointRoi && r.roiNumber === c.roiNumber)
                ? c
                : { ...c, roiNumber: null }
        );
    }, [bounding.roiNumber, roiArray, setBounding]);

    const activeBoundingRoi = bounding.enabled ? bounding.roiNumber : null;
    useEffect(() => {
        const existing = roiArray.map((r) => r.roiNumber);
        setCollision((c) => {
            const next = dropBoundingFromProtected(
                pruneProtectedToExisting(c.roiNumbers, existing),
                activeBoundingRoi
            );
            return next === c.roiNumbers ? c : { ...c, roiNumbers: next };
        });
    }, [activeBoundingRoi, collision.roiNumbers, roiArray, setCollision]);
}
